import { useState } from "react";
import { MailWarning, Check, RefreshCw, X } from "@/components/icons";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Yellow banner shown to logged-in users whose email isn't verified yet.
 * Includes a "Reenviar correo" button. Dismissible per-session.
 */
export default function EmailVerifyBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [sending, setSending] = useState(false);

  if (!user) return null;
  if (user.email_verified) return null;
  // Only doctors self-register through email. Other roles are seeded.
  if (user.role !== "doctor") return null;
  if (hidden) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(`Correo de verificación enviado a ${user.email}`);
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "No pudimos enviar el correo. Inténtalo de nuevo en unos minutos."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="email-verify-banner"
      className="px-4 sm:px-6 pt-3"
    >
      <div className="max-w-[1400px] mx-auto rounded-[16px] bg-white border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.03),0_4px_12px_-6px_rgba(0,0,0,0.05)] px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-full bg-[#E5F0FF] flex items-center justify-center shrink-0">
          <MailWarning className="w-[18px] h-[18px] text-[#0071E3]" strokeWidth={1.75} weight="regular" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[13.5px] font-semibold text-black">
            Verifica tu correo electrónico
          </div>
          <div className="text-[12px] text-black/55 mt-0.5">
            Enviamos un enlace de verificación a{" "}
            <strong className="text-black/80">{user.email}</strong>. Revisa tu bandeja de entrada y la carpeta de spam.
          </div>
        </div>
        <button
          type="button"
          onClick={resend}
          disabled={sending}
          data-testid="resend-verification-btn"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[#0071E3] hover:bg-[#0077ED] disabled:opacity-60 text-white text-[12.5px] font-semibold transition"
        >
          {sending ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} weight="regular" /> Enviando…
            </>
          ) : (
            <>
              <Check className="w-3.5 h-3.5" strokeWidth={2} weight="regular" /> Reenviar correo
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => setHidden(true)}
          data-testid="dismiss-verify-banner"
          className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-black/[0.05] text-black/50 transition"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
        </button>
      </div>
    </div>
  );
}
